import type { ReactNode } from 'react'
import { cn } from '../../utils/cn'

export interface FormFieldProps {
  /** Id of the control this label points at. */
  id: string
  label: ReactNode
  hint?: ReactNode
  error?: ReactNode
  required?: boolean
  children: ReactNode
  className?: string
}

/**
 * Label above, control, then hint or error. The design system is explicit that
 * labels are always visible in label-caps and never collapse into a
 * placeholder — this wrapper is what enforces that across every control.
 */
export function FormField({ id, label, hint, error, required, children, className }: FormFieldProps) {
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <label htmlFor={id} className="font-label-caps text-label-caps uppercase text-on-surface-variant">
        {label}
        {required && (
          <span aria-hidden="true" className="ml-1 text-error">
            *
          </span>
        )}
      </label>
      {children}
      {hint && !error && (
        <p id={`${id}-hint`} className="font-body-md text-sm text-on-surface-variant/80">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} className="flex items-center gap-1 font-body-md text-sm text-error">
          <span className="material-symbols-outlined text-[16px]">error</span>
          {error}
        </p>
      )}
    </div>
  )
}
